import { basicResetReducer } from '@ackee/redux-utils';
import { createReducer } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/es/storage';

import { authSessionEnd } from '@/modules/auth/services/actions';

import type { SecretListId, SecretType } from '../../constants';
import type { ListSecret, SearchHistory, SearchSecretAttributeType, ViewFilter } from '../../types';
import {
    clearActiveSecretAttributeFilters,
    removeSearchHistory,
    setSearchHistory,
    setSecretsListOffset,
    setSecretsNameFilter,
    setSecretSortBy,
    setSecretsTotalCount,
    setSecretTypeFilter,
    setViewFilter,
    setViewFilterIds,
    setViewFilterSeeded,
    toggleActiveSecretAttributeFilter,
} from '../actions';

const MAX_HISTORY = 8;

export type SortBy = {
    field: keyof ListSecret | 'name';
    order: 'asc' | 'desc';
    comparator: 'string' | 'date' | 'number';
};

export type SecretsState = {
    activeSecretsAttributesFilters: {
        value: string;
        label: string;
        type: SearchSecretAttributeType;
    }[];
    secretsFilter: string;
    secretsListOffset: {
        offset: number;
        listId: SecretListId;
    } | null;
    viewFilter: ViewFilter | null;
    viewFilterIds: string[] | null;
    /** Whether the per-session default view has been applied — see the seedViewFilter saga. */
    viewFilterSeeded: boolean;
    searchHistory: SearchHistory[];
    totalCount: number;
    secretType: SecretType | null | 'passkey';
    sortBy: SortBy;
};

export const initialState: SecretsState = {
    activeSecretsAttributesFilters: [],
    secretsFilter: '',
    secretsListOffset: null,
    viewFilter: null,
    viewFilterIds: null,
    viewFilterSeeded: false,
    searchHistory: [],
    totalCount: 0,
    secretType: null,
    sortBy: {
        field: 'name',
        order: 'asc',
        comparator: 'string',
    },
} as const satisfies SecretsState;

const childReducer = createReducer(initialState, builder => {
    builder
        .addCase(setSecretsNameFilter, (state, action) => {
            state.secretsFilter = action.payload.value;
        })
        .addCase(setSecretsListOffset, (state, action) => {
            state.secretsListOffset = action.payload;
        })
        .addCase(toggleActiveSecretAttributeFilter, (state, action) => {
            const {
                payload: { active, ...newFilter },
            } = action;

            let activeFilters = state.activeSecretsAttributesFilters;

            if (active) {
                if (!activeFilters.some(activeFilter => activeFilter.type === newFilter.type && activeFilter.value === newFilter.value)) {
                    activeFilters.push(newFilter);
                }
            } else {
                activeFilters = activeFilters.filter(activeFilter => {
                    if (activeFilter.type !== newFilter.type) return true;
                    if (activeFilter.value !== newFilter.value) return true;
                    return false;
                });
            }

            state.activeSecretsAttributesFilters = activeFilters;
        })
        .addCase(clearActiveSecretAttributeFilters, state => {
            state.activeSecretsAttributesFilters = [];
        })
        .addCase(setSecretsTotalCount, (state, action) => {
            state.totalCount = action.payload.totalCount;
        })
        .addCase(setViewFilter, (state, action) => {
            state.viewFilter = action.payload.view;
        })
        .addCase(setViewFilterIds, (state, action) => {
            state.viewFilterIds = action.payload.ids;
        })
        .addCase(setViewFilterSeeded, state => {
            state.viewFilterSeeded = true;
        })
        .addCase(setSearchHistory, (state, action) => {
            const option = action.payload;
            const index = state.searchHistory.findIndex(({ value, type }) => type === option.type && value === option.value);

            if (index > -1) {
                state.searchHistory.splice(index, 1);
            }

            state.searchHistory.unshift(option);

            if (state.searchHistory.length > MAX_HISTORY) {
                state.searchHistory = state.searchHistory.slice(0, MAX_HISTORY);
            }
        })
        .addCase(removeSearchHistory, (state, action) => {
            const valueToRemove = action.payload.value;

            state.searchHistory = state.searchHistory.filter(({ value }) => value !== valueToRemove);
        })
        .addCase(setSecretTypeFilter, (state, action) => {
            state.secretType = action.payload.secretType;
        })
        .addCase(setSecretSortBy, (state, action) => {
            state.sortBy = action.payload;
        });
});

const resettedList = basicResetReducer(childReducer, authSessionEnd.toString());

export const reducer = persistReducer(
    {
        key: 'secrets.index',
        storage,
        whitelist: [
            'searchHistory',
            'viewFilter',
            'viewFilterIds',
            'viewFilterSeeded',
            'secretType',
            'sortBy',
        ] satisfies (keyof SecretsState)[],
    },
    resettedList,
);
